import { Facebook, Instagram, Globe, Star, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SelectedFacets } from "@/api/searchService";

// Quick-access filter chips shown under the search bar on /stores.
// They drive the same SelectedFacets state used by the facet sidebar, so toggling a
// chip and toggling the matching sidebar control stay in sync. Works in both browse
// and search mode (the backend supports these filters on both /api/stores and
// /api/search/stores).

const TOP_RATING = 4; // "الأعلى تقييماً" chip = rating >= 4

type Props = {
  selectedFacets: SelectedFacets;
  onFacetChange: (facets: SelectedFacets) => void;
};

type Chip = {
  key: string;
  label: string;
  icon: typeof Facebook;
  active: boolean;
  toggle: () => void;
};

export default function QuickFilters({ selectedFacets, onFacetChange }: Props) {
  const chips: Chip[] = [
    {
      key: "facebook",
      label: "فيسبوك",
      icon: Facebook,
      active: selectedFacets.hasFacebook,
      toggle: () =>
        onFacetChange({ ...selectedFacets, hasFacebook: !selectedFacets.hasFacebook }),
    },
    {
      key: "instagram",
      label: "انستجرام",
      icon: Instagram,
      active: selectedFacets.hasInstagram,
      toggle: () =>
        onFacetChange({ ...selectedFacets, hasInstagram: !selectedFacets.hasInstagram }),
    },
    {
      key: "website",
      label: "موقع إلكتروني",
      icon: Globe,
      active: selectedFacets.hasWebsite,
      toggle: () =>
        onFacetChange({ ...selectedFacets, hasWebsite: !selectedFacets.hasWebsite }),
    },
    {
      key: "top-rated",
      label: "الأعلى تقييماً",
      icon: Star,
      active: selectedFacets.minRating >= TOP_RATING,
      toggle: () =>
        onFacetChange({
          ...selectedFacets,
          minRating: selectedFacets.minRating >= TOP_RATING ? 0 : TOP_RATING,
        }),
    },
  ];

  const anyActive = chips.some((c) => c.active);

  const clearAll = () =>
    onFacetChange({
      ...selectedFacets,
      hasFacebook: false,
      hasInstagram: false,
      hasWebsite: false,
      minRating: 0,
    });

  return (
    <div dir="rtl" className="flex flex-wrap items-center gap-2 mb-6">
      {chips.map(({ key, label, icon: Icon, active, toggle }) => (
        <button
          key={key}
          type="button"
          onClick={toggle}
          aria-pressed={active}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-arabic transition-colors min-h-[40px]",
            active
              ? "bg-warmOrange-500 border-warmOrange-500 text-white hover:bg-warmOrange-600"
              : "bg-white border-border text-gray-700 hover:border-warmOrange-400 hover:text-warmOrange-600"
          )}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}

      {anyActive && (
        <button
          type="button"
          onClick={clearAll}
          className="inline-flex items-center gap-1 text-sm text-warmOrange-500 hover:text-warmOrange-600 font-arabic px-2 py-2 min-h-[40px]"
        >
          <X className="w-4 h-4" />
          مسح الفلاتر
        </button>
      )}
    </div>
  );
}
